import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Download,
  ZoomIn, ZoomOut, Eye, EyeOff, Layers, FolderDown, Loader2,
  SlidersHorizontal, Send, Cloud, Frame, Columns2
} from 'lucide-react';
import JSZip from 'jszip';
import { HdrMergePipeline, MediaItem, ProcessedImage, ViewMode } from '../types';
import { supabase, getMediaItems, getMediaItem, getListing, getThumbnailUrl, previewSourceFile, sourcePreviewUrl, saveEditedProcessedImage, getHdrMergePipeline } from '../services/supabase';
import { CurvesState, applyCurvesToImageUrlAsBlob } from '../services/curves';
import { VerticalsOptions, applyVerticalsToImageUrlAsBlob } from '../services/verticals';
import { SkyStyle, replaceSky, urlToBlob } from '../services/gemini';
import CurvesEditor from './CurvesEditor';
import BlueSkyEditor from './BlueSkyEditor';
import VerticalsEditor from './VerticalsEditor';
import HdrCrossSell from './HdrCrossSell';
import HdrBeforeAfter from './HdrBeforeAfter';
import { formatSentAt } from './ListingCard';
import { trackHdrEvent } from '../services/analytics';

interface StudioPageProps {
  userId: string;
  credits: number;
  onCreditUsed: () => void;
}

const StudioPage: React.FC<StudioPageProps> = ({ userId, credits, onCreditUsed }) => {
  const { mediaId } = useParams<{ mediaId: string }>();
  
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('after');
  const [zoom, setZoom] = useState(100);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isCurvesOpen, setIsCurvesOpen] = useState(false);
  const [isSavingCurves, setIsSavingCurves] = useState(false);
  const [isBlueSkyOpen, setIsBlueSkyOpen] = useState(false);
  const [isVerticalsOpen, setIsVerticalsOpen] = useState(false);
  const [isSavingVerticals, setIsSavingVerticals] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [mergePipeline, setMergePipeline] = useState<HdrMergePipeline | null>(null);

  useEffect(() => {
    getHdrMergePipeline().then(setMergePipeline);
  }, []);

  const isAutopix = mergePipeline === 'autopix';
  const processingNote = isAutopix
    ? 'Still processing. A full set usually takes 20–40 minutes.'
    : 'Still processing. Each image usually takes a couple of minutes.';

  const currentMedia = mediaItems[currentIndex];
  const latestProcessed = currentMedia?.processed_images?.[0];
  const sourceFile = previewSourceFile(currentMedia?.source_files);
  const trackedProcessed = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!latestProcessed?.id) return;
    if (trackedProcessed.current.has(latestProcessed.id)) return;
    trackedProcessed.current.add(latestProcessed.id);
    trackHdrEvent('hdr_image_processed', { remaining: undefined });
  }, [latestProcessed?.id]);

  useEffect(() => {
    loadMedia(true);
  }, [mediaId]);

  // Poll while inside Studio so queue progress feels continuous.
  // Include `pending` with sources but no processed output (queued, waiting for worker).
  useEffect(() => {
    const needsPoll = mediaItems.some((m) => {
      if (m.status === 'processing' || m.status === 'uploading') return true;
      if (m.status === 'pending') {
        const hasSources = (m.source_files?.length ?? 0) > 0;
        const hasProcessed = (m.processed_images?.length ?? 0) > 0;
        return hasSources && !hasProcessed;
      }
      return false;
    });
    if (!needsPoll) return;
    
    const timer = setInterval(() => {
      loadMedia(false); // silent refresh, no loading spinner
    }, 8000); // slower polling to reduce flicker
    return () => clearInterval(timer);
  }, [mediaId, mediaItems]);

  const loadMedia = async (showLoading = true) => {
    if (!mediaId) return;
    
    if (showLoading) {
      setIsLoading(true);
      setSentAt(null);
    }
    
    // mediaId could be listing ID or media item ID
    // Try loading as listing first
    const items = await getMediaItems(mediaId);
    
    if (items.length > 0) {
      setMediaItems(items);
      if (showLoading) {
        const listing = await getListing(items[0].listing_id);
        setSentAt(listing?.created_at ?? null);
      }
    } else {
      // Try as media item ID
      const item = await getMediaItem(mediaId);
      if (item) {
        setMediaItems([item]);
        if (showLoading) {
          const listing = await getListing(item.listing_id);
          setSentAt(listing?.created_at ?? null);
        }
      }
    }
    
    setIsLoading(false);
  };

  const handleDownload = async () => {
    const imageUrl = viewMode === 'before' 
      ? sourcePreviewUrl(sourceFile)
      : (latestProcessed?.url || sourcePreviewUrl(sourceFile));
    
    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hdr-photo-${currentMedia.id}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      trackHdrEvent('hdr_image_downloaded', {});
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleDownloadAll = async () => {
    if (isDownloadingAll || mediaItems.length === 0) return;

    setIsDownloadingAll(true);

    try {
      const zip = new JSZip();
      const listingId = mediaItems[0].listing_id;
      const listing = await getListing(listingId);
      const folderName = listing?.address || listing?.name || 'hdr-photos';

      const downloads = mediaItems.map(async (item, index) => {
        const previewSource = previewSourceFile(item.source_files);
        const imageUrl = item.processed_images?.[0]?.url
          || sourcePreviewUrl(previewSource);

        if (!imageUrl) return;

        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const ext = blob.type === 'image/png' ? 'png' : 'jpg';
          const paddedIndex = String(index + 1).padStart(2, '0');
          zip.file(`${paddedIndex}.${ext}`, blob);
        } catch (err) {
          console.error(`Failed to fetch image ${index + 1}:`, err);
        }
      });

      await Promise.all(downloads);

      if (Object.keys(zip.files).length === 0) {
        console.error('No images were added to the zip');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = folderName.replace(/[^a-zA-Z0-9-_ ]/g, '').trim().replace(/\s+/g, '-');
      a.download = `${safeName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download all error:', error);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const getImageUrl = (): string | null => {
    if (viewMode === 'before') {
      return sourcePreviewUrl(sourceFile);
    }
    if (latestProcessed?.url) return latestProcessed.url;
    return sourcePreviewUrl(sourceFile);
  };

  // Hand the finished photo off to a sister ReHub app (Photoclear / Staging).
  // We always send the enhanced result (or source fallback) and forward the
  // current session as SSO hash tokens so the user lands logged in.
  const handleSendToApp = async (baseUrl: string) => {
    const imageUrl =
      latestProcessed?.url ||
      sourcePreviewUrl(sourceFile);
    if (!imageUrl) return;

    const search = `?imageUrl=${encodeURIComponent(imageUrl)}`;
    let hash = '';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token && session?.refresh_token) {
        hash = `#sso_access_token=${encodeURIComponent(session.access_token)}&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
      }
    } catch (_) {
      // Fall back to plain navigation if the session can't be read.
    }
    window.open(`${baseUrl}${search}${hash}`, '_blank', 'noopener,noreferrer');
  };

  const canSend = !!(latestProcessed?.url || sourcePreviewUrl(sourceFile));

  // The image the curves editor works on: the current enhanced result (or the
  // source if nothing has been processed yet).
  const getEditableUrl = (): string | null => {
    if (latestProcessed?.url) return latestProcessed.url;
    return sourcePreviewUrl(sourceFile);
  };

  const canEdit = !!getEditableUrl() && !!currentMedia;

  const handleSaveCurves = async (curves: CurvesState) => {
    const editableUrl = getEditableUrl();
    if (!editableUrl || !currentMedia) return;

    setIsSavingCurves(true);
    try {
      const blob = await applyCurvesToImageUrlAsBlob(
        editableUrl,
        curves,
        'image/jpeg',
        0.95
      );
      const saved = await saveEditedProcessedImage(
        userId,
        currentMedia.id,
        blob,
        'curves',
        { curves }
      );
      if (!saved) {
        window.alert('Could not save the edit. Please try again.');
        return;
      }
      setIsCurvesOpen(false);
      setViewMode('after');
      await loadMedia(false);
    } catch (error: any) {
      console.error('Curves save error:', error);
      window.alert(
        `Could not save the edit: ${error?.message || 'unknown error'}`
      );
    } finally {
      setIsSavingCurves(false);
    }
  };

  const handleSaveVerticals = async (options: VerticalsOptions) => {
    const editableUrl = getEditableUrl();
    if (!editableUrl || !currentMedia) return;

    setIsSavingVerticals(true);
    try {
      const blob = await applyVerticalsToImageUrlAsBlob(
        editableUrl,
        options,
        'image/jpeg',
        0.95
      );
      const saved = await saveEditedProcessedImage(
        userId,
        currentMedia.id,
        blob,
        'verticals',
        { verticals: options }
      );
      if (!saved) {
        window.alert('Could not save the edit. Please try again.');
        return;
      }
      setIsVerticalsOpen(false);
      setViewMode('after');
      await loadMedia(false);
    } catch (error: any) {
      console.error('Verticals save error:', error);
      window.alert(
        `Could not save the edit: ${error?.message || 'unknown error'}`
      );
    } finally {
      setIsSavingVerticals(false);
    }
  };

  // AI sky generation: free — sends the current image to Gemini via the
  // gemini-image edge function and returns the result for preview.
  // Step 1: generate a new sky for preview and return the result as a data: URL
  // WITHOUT saving — the user accepts or discards it in the editor.
  const handleGenerateSky = async (style: SkyStyle): Promise<string> => {
    const editableUrl = getEditableUrl();
    if (!editableUrl || !currentMedia) {
      throw new Error('No image is available to edit.');
    }
    return await replaceSky(editableUrl, style);
  };

  // Step 2: the user accepted the previewed sky — persist it and refresh.
  const handleAcceptSky = async (resultDataUrl: string, style: SkyStyle) => {
    if (!currentMedia) return;
    const blob = await urlToBlob(resultDataUrl);
    const saved = await saveEditedProcessedImage(
      userId,
      currentMedia.id,
      blob,
      'blue_sky',
      { ai_sky_replacement: true, style }
    );
    if (!saved) {
      throw new Error('Failed to save the new image.');
    }
    setIsBlueSkyOpen(false);
    setViewMode('after');
    await loadMedia(false);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-950">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (mediaItems.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-950 text-center px-4">
        <Layers className="w-16 h-16 text-slate-600 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">No media found</h2>
        <p className="text-slate-400 mb-6">This listing doesn't have any images yet.</p>
        <Link
          to="/listings"
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Listings
        </Link>
      </div>
    );
  }

  return (
    <div className="flex-1 flex bg-slate-950 min-w-0">
      {/* Main Studio Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="h-12 bg-slate-900/50 border-b border-slate-800 flex items-center gap-2 px-3 overflow-x-auto">
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/listings"
              className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
              title="Back to listings"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden xl:inline text-sm">Back</span>
            </Link>

            {mediaItems.length > 1 && (
              <div className="flex items-center">
                <button
                  onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                  disabled={currentIndex === 0}
                  className="p-1 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous image"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-400 tabular-nums min-w-[2.75rem] text-center">
                  {currentIndex + 1}/{mediaItems.length}
                </span>
                <button
                  onClick={() => setCurrentIndex(Math.min(mediaItems.length - 1, currentIndex + 1))}
                  disabled={currentIndex === mediaItems.length - 1}
                  className="p-1 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next image"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center bg-slate-800 rounded-lg p-0.5 shrink-0">
            {([
              { mode: 'before' as ViewMode, icon: EyeOff, label: 'Before' },
              { mode: 'after' as ViewMode, icon: Eye, label: 'After' },
              { mode: 'compare' as ViewMode, icon: Columns2, label: 'Compare' },
            ]).map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  viewMode === mode
                    ? 'bg-brand-500 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
                title={label}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 min-w-2 flex items-center px-2 overflow-hidden">
            {sentAt && (
              <span className="text-xs text-slate-500 truncate" title={`Sent ${formatSentAt(sentAt)}`}>
                Sent {formatSentAt(sentAt)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <div className="hidden lg:flex items-center">
              <button
                onClick={() => setZoom(Math.max(50, zoom - 25))}
                className="p-1.5 text-slate-400 hover:text-white transition-colors"
                title="Zoom out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                onClick={() => setZoom(100)}
                className="text-xs text-slate-400 w-10 text-center hover:text-white transition-colors"
                title="Reset zoom"
              >
                {zoom}%
              </button>
              <button
                onClick={() => setZoom(Math.min(200, zoom + 25))}
                className="p-1.5 text-slate-400 hover:text-white transition-colors"
                title="Zoom in"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center bg-slate-800 rounded-lg p-0.5">
              <button
                onClick={() => setIsCurvesOpen(true)}
                disabled={!canEdit}
                className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Curves"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsBlueSkyOpen(true)}
                disabled={!canEdit}
                className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Blue Sky"
              >
                <Cloud className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsVerticalsOpen(true)}
                disabled={!canEdit}
                className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Vertical & lens correction"
              >
                <Frame className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center bg-slate-800 rounded-lg p-0.5">
              <button
                onClick={handleDownload}
                className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                title="Download this image"
              >
                <Download className="w-4 h-4" />
              </button>
              {mediaItems.length > 1 && (
                <button
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll}
                  className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isDownloadingAll ? 'Zipping…' : 'Download all'}
                >
                  {isDownloadingAll ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FolderDown className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <HdrCrossSell
          imageUrl={latestProcessed?.url || sourcePreviewUrl(sourceFile)}
          disabled={!canSend}
        />

        {/* Image Preview Area */}
        <div className="flex-1 min-w-0 overflow-auto flex items-center justify-center p-4 bg-slate-950">
          {viewMode === 'compare' && sourcePreviewUrl(sourceFile) && latestProcessed?.url ? (
            <HdrBeforeAfter
              beforeUrl={sourcePreviewUrl(sourceFile)!}
              afterUrl={latestProcessed.url}
            />
          ) : getImageUrl() ? (
            <div className="flex max-w-full flex-col items-center">
              <img
                key={getImageUrl()!}
                src={getImageUrl()!}
                alt="Preview"
                style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'center' }}
                className="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
              />
              {!latestProcessed?.url && (currentMedia?.status === 'processing' || currentMedia?.status === 'pending') && (
                <p className="mt-3 text-center text-sm text-slate-400">
                  {processingNote}
                </p>
              )}
            </div>
          ) : currentMedia?.status === 'processing' || currentMedia?.status === 'pending' ? (
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-sky-500 mx-auto mb-4 animate-spin" />
              <p className="text-slate-300 font-medium">Processing HDR image</p>
              {sentAt && (
                <p className="text-sm text-slate-400 mt-1">Sent {formatSentAt(sentAt)}</p>
              )}
              <p className="text-sm text-slate-500 mt-1">
                {isAutopix
                  ? 'A full set usually takes 20–40 minutes.'
                  : 'Images are processed one at a time, usually a couple of minutes each.'}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <Layers className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No preview available</p>
            </div>
          )}
        </div>

        {/* Bottom: Thumbnail Strip */}
        {mediaItems.length > 1 && (
          <div className="h-24 bg-slate-900/50 border-t border-slate-800 flex items-center gap-2 px-4 overflow-x-auto">
            {mediaItems.map((item, index) => {
              const thumbSource = previewSourceFile(item.source_files);
              const rawThumbUrl = item.processed_images?.[0]?.url 
                || sourcePreviewUrl(thumbSource);
              const thumb = getThumbnailUrl(rawThumbUrl, { width: 150, height: 150 });
              
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentIndex 
                      ? 'border-brand-500 ring-2 ring-brand-500/30' 
                      : 'border-transparent hover:border-slate-600'
                  }`}
                >
                  {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                      <Layers className="w-6 h-6 text-slate-600" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isCurvesOpen && getEditableUrl() && (
        <CurvesEditor
          imageUrl={getEditableUrl()!}
          isSaving={isSavingCurves}
          onSave={handleSaveCurves}
          onCancel={() => !isSavingCurves && setIsCurvesOpen(false)}
        />
      )}

      {isBlueSkyOpen && getEditableUrl() && (
        <BlueSkyEditor
          imageUrl={getEditableUrl()!}
          onGenerate={handleGenerateSky}
          onAccept={handleAcceptSky}
          onCancel={() => setIsBlueSkyOpen(false)}
        />
      )}

      {isVerticalsOpen && getEditableUrl() && (
        <VerticalsEditor
          imageUrl={getEditableUrl()!}
          isSaving={isSavingVerticals}
          onSave={handleSaveVerticals}
          onCancel={() => !isSavingVerticals && setIsVerticalsOpen(false)}
        />
      )}
    </div>
  );
};

export default StudioPage;
